import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  UserPlus, Shield, Search, MessageSquare, MapPin, CheckCircle,
  DollarSign, Star, Zap, Clock, Bell, Award, FileText,
  Users, ThumbsUp, CreditCard, Play, Pause, RotateCcw
} from "lucide-react";

import CursorEffect from "./CursorEffect";

/* ─── STEP DATA ─── */
const HERO_STEPS = [
  {
    id: "signup", icon: UserPlus, title: "Create Your Account",
    sub: "One-tap Google sign-in, no passwords needed",
    color: "#8448fc", dur: 3500,
  },
  {
    id: "verify", icon: Shield, title: "Get Verified",
    sub: "Upload ID and selfie for trust",
    color: "#047857", dur: 4000,
  },
  {
    id: "browse", icon: Search, title: "Browse Jobs",
    sub: "Find jobs near you on map or list",
    color: "#8b5cf6", dur: 4000,
  },
  {
    id: "bid", icon: MessageSquare, title: "Place Your Bid",
    sub: "Name your price and win the job",
    color: "#b45309", dur: 4000,
  },
  {
    id: "hired", icon: Bell, title: "Get Hired",
    sub: "Client accepts — funds held in escrow",
    color: "#047857", dur: 3500,
  },
  {
    id: "track", icon: MapPin, title: "Complete the Job",
    sub: "GPS tracking keeps everyone informed",
    color: "#0ea5e9", dur: 4000,
  },
  {
    id: "paid", icon: DollarSign, title: "Get Paid",
    sub: "Direct deposit about 4 days after the job",
    color: "#047857", dur: 4000,
  },
];

const CLIENT_STEPS = [
  {
    id: "signup", icon: UserPlus, title: "Create Your Account",
    sub: "One-tap Google sign-in, free forever",
    color: "#8448fc", dur: 3500,
  },
  {
    id: "post", icon: FileText, title: "Post a Job",
    sub: "Describe what you need, set your budget",
    color: "#0ea5e9", dur: 4000,
  },
  {
    id: "review", icon: Users, title: "Review Bids",
    sub: "Compare verified Heroes and their offers",
    color: "#8b5cf6", dur: 4000,
  },
  {
    id: "hire", icon: ThumbsUp, title: "Hire a Hero",
    sub: "Accept the best bid — funds held securely",
    color: "#b45309", dur: 3500,
  },
  {
    id: "track", icon: MapPin, title: "Track Progress",
    sub: "Watch your Hero arrive in real time",
    color: "#0ea5e9", dur: 4000,
  },
  {
    id: "rate", icon: Star, title: "Rate & Pay",
    sub: "Approve the work and leave a review",
    color: "#047857", dur: 4000,
  },
];

/* ─── PHONE SCREEN RENDERER ─── */
const PhoneScreen = ({ step, progress, mode }) => {
  switch (step.id) {
    case "signup":
      return (
        <div className="js-screen">
          <div className="js-scr-hdr"><span>Welcome</span></div>
          <div className="js-auth-block">
            <div className="js-auth-avatar">
              <UserPlus size={22} strokeWidth={1.5} />
            </div>
            <p className="js-auth-txt">Sign in to get started</p>
            <div className={`js-google-btn ${progress > 0.4 ? "pressed" : ""}`}>
              <svg width="15" height="15" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
              <span>Continue with Google</span>
            </div>
            {progress > 0.65 && <div className="js-auth-ok"><CheckCircle size={13} /> Account created</div>}
          </div>
        </div>
      );

    case "verify":
      return (
        <div className="js-screen">
          <div className="js-scr-hdr"><span>Identity Verification</span></div>
          <div className="js-verify-list">
            {[
              { label: "Upload ID", thresh: 0.2 },
              { label: "Take Selfie", thresh: 0.5 },
              { label: "Verified", thresh: 0.8 },
            ].map((v, i) => (
              <div key={i} className={`js-verify-row ${progress > v.thresh ? "done" : ""}`}>
                <div className="js-vr-icon">{progress > v.thresh ? <CheckCircle size={12} /> : <span>{i + 1}</span>}</div>
                <span>{v.label}</span>
              </div>
            ))}
          </div>
          {progress > 0.8 && (
            <div className="js-badge-pill"><Shield size={14} /> Verified</div>
          )}
        </div>
      );

    case "browse":
      return (
        <div className="js-screen">
          <div className="js-scr-hdr"><span>Browse Jobs</span></div>
          <div className="js-map-box">
            <div className="js-map-grid-bg" />
            {[{ x: 25, y: 20, d: 0.15 }, { x: 60, y: 35, d: 0.3 }, { x: 40, y: 60, d: 0.45 }, { x: 72, y: 18, d: 0.35 }, { x: 18, y: 48, d: 0.55 }].map((p, i) => (
              progress > p.d && <div key={i} className="js-map-pin" style={{ left: `${p.x}%`, top: `${p.y}%` }}><MapPin size={12} /></div>
            ))}
            {progress > 0.4 && <div className="js-map-me" style={{ left: "48%", top: "42%" }}><div className="js-me-dot" /></div>}
          </div>
        </div>
      );

    case "bid":
      return (
        <div className="js-screen">
          <div className="js-scr-hdr"><span>Place a Bid</span></div>
          <div className="js-bid-card">
            <strong>Deep House Cleaning</strong>
            <span className="js-bid-budget">Budget: $80–$120</span>
          </div>
          <div className="js-bid-amt-area">
            <span className="js-bid-label">Your Bid</span>
            <div className="js-bid-amt">
              <span className="js-bid-dollar">$</span>
              <span className="js-bid-num">{progress > 0.3 ? "95" : "—"}</span>
            </div>
          </div>
          {progress > 0.55 && <div className="js-bid-msg">"I'll bring all supplies and have 5 years experience."</div>}
          {progress > 0.75 && <div className="js-bid-sent"><CheckCircle size={13} /> Bid Submitted</div>}
        </div>
      );

    case "hired":
      return (
        <div className="js-screen js-screen-center">
          {progress > 0.25 && (
            <div className="js-notif-card">
              <div className="js-notif-icon"><Bell size={16} /></div>
              <div><strong>You got hired!</strong><p>Sarah M. accepted your bid</p></div>
            </div>
          )}
          {progress > 0.55 && (
            <div className="js-chat-mini">
              <div className="js-chat-bub js-chat-l">Hi! Looking forward to it</div>
              <div className="js-chat-bub js-chat-r">See you at 9 AM!</div>
            </div>
          )}
          {progress > 0.8 && <div className="js-escrow-pill"><Shield size={11} /> $95 in escrow</div>}
        </div>
      );

    case "track": {
      const t = Math.min(progress / 0.85, 1);
      const hx = 15 + t * 63;
      const hy = 55 - Math.sin(t * Math.PI) * 18;
      return (
        <div className="js-screen">
          <div className="js-scr-hdr"><span>Job Tracking</span></div>
          <div className="js-track-map">
            <div className="js-map-grid-bg" />
            <svg className="js-route-svg" viewBox="0 0 240 140" preserveAspectRatio="none">
              <path d="M 36 77 C 72 30, 150 30, 192 63" fill="none" stroke="rgba(132, 72, 252,0.12)" strokeWidth="3" strokeLinecap="round" />
              <path d="M 36 77 C 72 30, 150 30, 192 63" fill="none" stroke="#8448fc" strokeWidth="3" strokeLinecap="round"
                strokeDasharray="200" strokeDashoffset={200 - t * 200} style={{ transition: "stroke-dashoffset 0.12s linear" }} />
            </svg>
            <div className="js-trk-start" style={{ left: "15%", top: "55%" }}><div className="js-trk-dot-a" /></div>
            <div className="js-trk-hero" style={{ left: `${hx}%`, top: `${hy}%` }}><Zap size={9} /></div>
            <div className="js-trk-dest" style={{ left: "80%", top: "45%" }}><MapPin size={12} /></div>
          </div>
          <div className="js-status-row">
            <div className={`js-status-led ${progress > 0.1 ? "on" : ""}`} />
            <span>{progress < 0.3 ? "En Route" : progress < 0.6 ? "Arriving" : progress < 0.85 ? "In Progress" : "Completed"}</span>
          </div>
          {progress > 0.85 && <div className="js-complete-tag"><CheckCircle size={14} /> Job Complete</div>}
        </div>
      );
    }

    case "paid":
      return (
        <div className="js-screen js-screen-center">
          {progress > 0.15 && (
            <div className="js-earn-card">
              <span className="js-earn-lbl">Your Earnings</span>
              <span className="js-earn-amt">$85.50</span>
              <span className="js-earn-note">$95.00 – 10% fee</span>
            </div>
          )}
          {progress > 0.45 && (
            <div className="js-payout-steps">
              {["Job completed", "Escrow released", "Deposited to bank"].map((s, i) => (
                <div key={s} className={`js-pay-step ${progress > 0.45 + i * 0.18 ? "done" : ""}`}>
                  <CheckCircle size={10} /><span>{s}</span>
                </div>
              ))}
            </div>
          )}
          {progress > 0.85 && <div className="js-paid-pill"><DollarSign size={14} /> Paid to your bank</div>}
        </div>
      );

    /* ─── CLIENT-ONLY SCREENS ─── */
    case "post":
      return (
        <div className="js-screen">
          <div className="js-scr-hdr"><span>Post a Job</span></div>
          <div className="js-form-fields">
            <div className={`js-form-row ${progress > 0.15 ? "filled" : ""}`}>
              <span className="js-form-label">Title</span>
              {progress > 0.15 && <span className="js-form-val">Kitchen Deep Clean</span>}
            </div>
            <div className={`js-form-row ${progress > 0.35 ? "filled" : ""}`}>
              <span className="js-form-label">Budget</span>
              {progress > 0.35 && <span className="js-form-val">$100 – $150</span>}
            </div>
            <div className={`js-form-row ${progress > 0.5 ? "filled" : ""}`}>
              <span className="js-form-label">Location</span>
              {progress > 0.5 && <span className="js-form-val">Toronto, ON</span>}
            </div>
            <div className={`js-form-row ${progress > 0.65 ? "filled" : ""}`}>
              <span className="js-form-label">Details</span>
              {progress > 0.65 && <span className="js-form-val js-form-val-sm">Full kitchen including oven, fridge...</span>}
            </div>
          </div>
          {progress > 0.82 && <div className="js-form-submit"><CheckCircle size={13} /> Job Posted</div>}
        </div>
      );

    case "review":
      return (
        <div className="js-screen">
          <div className="js-scr-hdr"><span>Review Bids</span></div>
          <div className="js-bids-list">
            {[
              { name: "Mike R.", price: "$110", rating: "4.9", delay: 0.15 },
              { name: "Sara K.", price: "$95", rating: "5.0", delay: 0.35 },
              { name: "Jake T.", price: "$125", rating: "4.7", delay: 0.55 },
            ].map((b, i) => (
              progress > b.delay && (
                <div key={i} className={`js-bid-row ${progress > 0.75 && i === 1 ? "selected" : ""}`}>
                  <div className="js-bidder-info">
                    <div className="js-bidder-avatar">{b.name[0]}</div>
                    <div>
                      <strong>{b.name}</strong>
                      <span className="js-bidder-rating"><Star size={9} fill="#b45309" color="#b45309" /> {b.rating}</span>
                    </div>
                  </div>
                  <span className="js-bidder-price">{b.price}</span>
                </div>
              )
            ))}
          </div>
        </div>
      );

    case "hire":
      return (
        <div className="js-screen js-screen-center">
          {progress > 0.2 && (
            <div className="js-hire-card">
              <div className="js-hire-avatar">S</div>
              <strong>Sara K.</strong>
              <div className="js-hire-stars">
                {[...Array(5)].map((_, i) => <Star key={`star-${i}`} size={12} fill="#b45309" color="#b45309" />)}
              </div>
              <span className="js-hire-price">$95</span>
            </div>
          )}
          {progress > 0.5 && (
            <div className={`js-hire-btn ${progress > 0.7 ? "confirmed" : ""}`}>
              {progress > 0.7 ? <><CheckCircle size={13} /> Hired!</> : "Confirm Hire"}
            </div>
          )}
          {progress > 0.8 && <div className="js-escrow-pill"><Shield size={11} /> $95 secured in escrow</div>}
        </div>
      );

    case "rate":
      return (
        <div className="js-screen js-screen-center">
          {progress > 0.15 && (
            <div className="js-rate-block">
              <p className="js-rate-label">How was your experience?</p>
              <div className="js-rate-stars">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={22} fill={progress > 0.25 + i * 0.08 ? "#b45309" : "none"} color={progress > 0.25 + i * 0.08 ? "#b45309" : "#475569"} />
                ))}
              </div>
            </div>
          )}
          {progress > 0.6 && (
            <div className="js-rate-comment">"Absolutely spotless. Sara did an amazing job!"</div>
          )}
          {progress > 0.8 && <div className="js-rate-done"><CheckCircle size={14} /> Review Submitted</div>}
        </div>
      );

    default:
      return null;
  }
};

/* ─── MAIN COMPONENT ─── */
const JourneyShowcase = () => {
  const [mode, setMode] = useState("hero");
  const [currentIdx, setCurrentIdx] = useState(0);
  const [stepProg, setStepProg] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const rafRef = useRef(null);
  const startRef = useRef(null);
  const pausedRef = useRef(0);
  const sectionRef = useRef(null);

  const steps = mode === "hero" ? HERO_STEPS : CLIENT_STEPS;
  const total = steps.reduce((s, st) => s + st.dur, 0);

  const getAt = useCallback((elapsed) => {
    let acc = 0;
    for (let i = 0; i < steps.length; i++) {
      if (elapsed < acc + steps[i].dur) return { idx: i, prog: (elapsed - acc) / steps[i].dur };
      acc += steps[i].dur;
    }
    return { idx: steps.length - 1, prog: 1 };
  }, [steps]);

  const tick = useCallback((ts) => {
    if (!startRef.current) startRef.current = ts;
    const elapsed = Math.min(pausedRef.current + (ts - startRef.current), total);
    const { idx, prog } = getAt(elapsed);
    setCurrentIdx(idx);
    setStepProg(prog);
    if (elapsed < total) {
      rafRef.current = requestAnimationFrame(tick);
    } else {
      setPlaying(false);
    }
  }, [getAt, total]);

  useEffect(() => {
    if (playing) {
      startRef.current = null;
      rafRef.current = requestAnimationFrame(tick);
    }
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [playing, tick]);

  // Auto-start when scrolled into view
  useEffect(() => {
    if (hasStarted) return;
    const el = sectionRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setHasStarted(true);
        setPlaying(true);
        obs.disconnect();
      }
    }, { threshold: 0.35 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasStarted]);

  const switchMode = (m) => {
    if (m === mode) return;
    cancelAnimationFrame(rafRef.current);
    setMode(m);
    setCurrentIdx(0);
    setStepProg(0);
    pausedRef.current = 0;
    setPlaying(true);
  };

  const togglePlay = () => {
    const elapsed = steps.slice(0, currentIdx).reduce((s, st) => s + st.dur, 0) + stepProg * steps[currentIdx].dur;
    if (!playing && elapsed >= total - 50) {
      pausedRef.current = 0;
      setCurrentIdx(0);
      setStepProg(0);
      setPlaying(true);
      return;
    }
    if (playing) {
      cancelAnimationFrame(rafRef.current);
      pausedRef.current = elapsed;
      setPlaying(false);
    } else {
      setPlaying(true);
    }
  };

  const restart = () => {
    cancelAnimationFrame(rafRef.current);
    pausedRef.current = 0;
    setCurrentIdx(0);
    setStepProg(0);
    setPlaying(true);
  };

  const step = steps[currentIdx];
  const globalProg = (steps.slice(0, currentIdx).reduce((s, st) => s + st.dur, 0) + stepProg * step.dur) / total;

  return (
    <section className="js-section" ref={sectionRef} data-testid="journey-showcase" style={{ position: 'relative', overflow: 'hidden' }}>
      <CursorEffect type="spotlight" />
      <style>{journeyCSS}</style>

      {/* Section heading */}
      <div className="js-section-head">
        <div className="js-section-badge"><Zap size={14} /><span>Instant Bids</span></div>
        <h2 className="js-section-title">taskeri<span style={{fontSize:'0.55em', verticalAlign: 'super', lineHeight: 0}}>®</span> in action</h2>
        <p className="js-section-sub">Watch how easy to use taskeri<sup style={{fontSize:'0.55em'}}>®</sup> is with the power of AI</p>
      </div>

      {/* Switcher */}
      <div className="js-header">
        <div className="js-switcher" data-testid="journey-switcher">
          <button className={`js-sw-btn ${mode === "hero" ? "active" : ""}`} onClick={() => switchMode("hero")} data-testid="journey-switch-hero">
            <Zap size={14} /> Hero Journey
          </button>
          <button className={`js-sw-btn ${mode === "client" ? "active" : ""}`} onClick={() => switchMode("client")} data-testid="journey-switch-client">
            <Users size={14} /> Client Journey
          </button>
          <div className="js-sw-slider" style={{ transform: mode === "client" ? "translateX(100%)" : "translateX(0)" }} />
        </div>
      </div>

      {/* Main content area */}
      <div className="js-stage">
        {/* Left — step list */}
        <div className="js-steps-col">
          {steps.map((s, i) => {
            const Icon = s.icon;
            const isActive = i === currentIdx;
            const isDone = i < currentIdx;
            return (
              <div key={`${mode}-${s.id}`} className={`js-step-item ${isActive ? "active" : ""} ${isDone ? "done" : ""}`} data-testid={`journey-step-${s.id}`}>
                <div className="js-step-line-wrap">
                  <div className="js-step-icon-circle" style={{ borderColor: isActive || isDone ? s.color : "rgba(255,255,255,0.15)", color: isActive || isDone ? s.color : "#94a3b8" }}>
                    {isDone ? <CheckCircle size={14} /> : <Icon size={14} />}
                  </div>
                  {i < steps.length - 1 && (
                    <div className="js-step-connector">
                      <div className="js-step-conn-fill" style={{
                        height: isDone ? "100%" : isActive ? `${stepProg * 100}%` : "0%",
                        background: s.color,
                      }} />
                    </div>
                  )}
                </div>
                <div className="js-step-text">
                  <span className="js-step-title" style={{ color: isActive ? "#f1f5f9" : isDone ? "#94a3b8" : "#94a3b8" }}>{s.title}</span>
                  {isActive && <span className="js-step-sub">{s.sub}</span>}
                </div>
              </div>
            );
          })}
        </div>

        {/* Center — phone */}
        <div className="js-phone-col">
          <div className="js-phone-wrap">
            {/* Neon glow */}
            <div className="js-phone-neon" style={{ background: step.color, boxShadow: `0 0 80px ${step.color}66, 0 0 160px ${step.color}33` }} />
            {/* Ambient glow */}
            <div className="js-phone-glow" style={{ background: `radial-gradient(ellipse at 50% 50%, ${step.color}25 0%, transparent 70%)` }} />
            <div className="js-phone" data-testid="journey-phone-mockup">
              <div className="js-phone-notch" />
              <PhoneScreen step={step} progress={stepProg} mode={mode} />
              <div className="js-phone-bar" />
            </div>
          </div>
        </div>

        {/* Right — current step highlight (large screens) */}
        <div className="js-info-col">
          <div className="js-info-card" key={`${mode}-${step.id}`}>
            <div className="js-info-step-num" style={{ color: step.color }}>
              Step {currentIdx + 1}<span className="js-info-of"> of {steps.length}</span>
            </div>
            <h3 className="js-info-title">{step.title}</h3>
            <p className="js-info-sub">{step.sub}</p>
          </div>
        </div>
      </div>

      {/* Timeline controls */}
      <div className="js-timeline" data-testid="journey-timeline">
        <button className="js-tl-btn" onClick={togglePlay} data-testid="journey-play-btn">
          {playing ? <Pause size={16} /> : globalProg >= 0.99 ? <RotateCcw size={16} /> : <Play size={16} />}
        </button>
        {globalProg > 0 && globalProg < 0.99 && (
          <button className="js-tl-btn js-tl-sm" onClick={restart}><RotateCcw size={12} /></button>
        )}
        <div className="js-tl-bar">
          {steps.map((s, i) => (
            <div key={`${mode}-bar-${i}`} className="js-tl-seg" style={{ flex: s.dur }}>
              <div className="js-tl-seg-fill" style={{
                width: i < currentIdx ? "100%" : i === currentIdx ? `${stepProg * 100}%` : "0%",
                background: step.color,
              }} />
            </div>
          ))}
        </div>
        <span className="js-tl-time">{Math.ceil(globalProg * total / 1000)}s / {Math.round(total / 1000)}s</span>
      </div>
    </section>
  );
};

/* ─── CSS ─── */
const journeyCSS = `
  .js-section {
    padding: 64px 0 52px;
    position: relative;
    overflow: hidden;
    background: #0f172a;
    margin-bottom: -4px;
    padding-bottom: 56px;
  }

  /* Header */
  .js-section-head { text-align: center; margin-bottom: 32px; padding: 0 24px; }
  .js-section-badge {
    display: inline-flex; align-items: center; gap: 6px;
    background: rgba(132, 72, 252,0.1); border: 1px solid rgba(132, 72, 252,0.2);
    padding: 5px 12px; border-radius: 100px; color: #818cf8;
    font-size: 12px; font-weight: 500; margin-bottom: 14px;
  }
  .js-section-title {
    font-size: 32px; font-weight: 700;
    margin: 0 0 10px; letter-spacing: -0.3px;
    background: linear-gradient(135deg, #e2e8f0 0%, #94a3b8 100%);
    -webkit-background-clip: text; -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  .js-section-sub { font-size: 16px; color: #94a3b8; margin: 0; }

  /* Switcher header */
  .js-header { text-align: center; margin-bottom: 40px; padding: 0 24px; }

  /* Switcher */
  .js-switcher {
    display: inline-flex; position: relative; background: rgba(255,255,255,0.04);
    border: 1px solid rgba(255,255,255,0.08); border-radius: 14px; padding: 4px;
  }
  .js-sw-btn {
    display: flex; align-items: center; gap: 7px;
    padding: 11px 28px; border-radius: 11px; border: none; background: transparent;
    color: #64748b; font-size: 14px; font-weight: 600; cursor: pointer;
    position: relative; z-index: 2; transition: color 0.3s;
  }
  .js-sw-btn.active { color: #fff; }
  .js-sw-btn:hover:not(.active) { color: #94a3b8; }
  .js-sw-slider {
    position: absolute; top: 4px; left: 4px; width: calc(50% - 4px); height: calc(100% - 8px);
    background: #8448fc; border-radius: 11px;
    transition: transform 0.35s cubic-bezier(0.16, 1, 0.3, 1);
    z-index: 1;
    box-shadow: 0 2px 12px rgba(132, 72, 252,0.4);
  }

  /* Stage */
  .js-stage {
    display: flex; align-items: center; justify-content: center;
    gap: 48px; max-width: 1100px; margin: 0 auto; padding: 0 32px;
    min-height: 480px;
  }

  /* Step list (left) */
  .js-steps-col { width: 240px; flex-shrink: 0; }
  .js-step-item { display: flex; gap: 14px; }
  .js-step-line-wrap { display: flex; flex-direction: column; align-items: center; flex-shrink: 0; }
  .js-step-icon-circle {
    width: 32px; height: 32px; border-radius: 10px;
    border: 1.5px solid; display: flex; align-items: center; justify-content: center;
    transition: all 0.4s; flex-shrink: 0;
    background: rgba(255,255,255,0.02);
  }
  .js-step-connector { width: 2px; flex: 1; min-height: 16px; background: rgba(255,255,255,0.06); border-radius: 1px; overflow: hidden; }
  .js-step-conn-fill { width: 100%; border-radius: 1px; transition: height 0.15s linear; }
  .js-step-text { padding: 4px 0 20px; min-width: 0; }
  .js-step-title { display: block; font-size: 13px; font-weight: 600; transition: color 0.3s; line-height: 1.4; }
  .js-step-sub {
    display: block; font-size: 11px; color: #64748b; margin-top: 2px; line-height: 1.4;
    animation: js-fade 0.3s ease;
  }

  /* Phone column (center) */
  .js-phone-col { flex-shrink: 0; position: relative; }
  .js-phone-wrap { position: relative; }
  .js-phone-glow {
    position: absolute; width: 360px; height: 360px; top: 50%; left: 50%;
    transform: translate(-50%, -50%); border-radius: 50%; transition: background 1.2s ease;
    pointer-events: none; z-index: 0;
  }
  .js-phone-neon {
    position: absolute; top: 50%; left: 50%;
    transform: translate(-50%, -50%);
    width: 260px; height: 500px;
    border-radius: 36px;
    pointer-events: none; z-index: 0;
    filter: blur(40px);
    opacity: 0.55;
    transition: background 1.2s ease, box-shadow 1.2s ease;
    animation: js-neon-pulse 3s ease-in-out infinite;
  }
  @keyframes js-neon-pulse {
    0%, 100% { opacity: 0.5; filter: blur(40px); }
    50% { opacity: 0.7; filter: blur(48px); }
  }
  .js-phone {
    width: 240px; height: 480px; background: #0f1629;
    border: 1.5px solid rgba(255,255,255,0.08); border-radius: 32px;
    position: relative; overflow: hidden; z-index: 1;
    box-shadow: 0 24px 64px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.04);
  }
  .js-phone-notch {
    position: absolute; top: 6px; left: 50%; transform: translateX(-50%);
    width: 72px; height: 20px; background: #0a0e1a; border-radius: 0 0 12px 12px; z-index: 10;
  }
  .js-phone-bar {
    position: absolute; bottom: 7px; left: 50%; transform: translateX(-50%);
    width: 80px; height: 3px; background: rgba(255,255,255,0.15); border-radius: 3px;
  }

  /* Phone screens */
  .js-screen {
    position: absolute; inset: 0; padding: 38px 14px 22px;
    display: flex; flex-direction: column; align-items: center;
    overflow: hidden;
    animation: js-screen-in 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  }
  .js-screen-center { justify-content: center; }
  .js-scr-hdr {
    width: 100%; text-align: center; padding-bottom: 8px;
    border-bottom: 1px solid rgba(255,255,255,0.05); margin-bottom: 10px; flex-shrink: 0;
  }
  .js-scr-hdr span { font-size: 12px; font-weight: 600; color: #e2e8f0; }

  /* Auth screen */
  .js-auth-block { text-align: center; margin-top: 12px; }
  .js-auth-avatar {
    width: 44px; height: 44px; border-radius: 14px; margin: 0 auto 10px;
    background: linear-gradient(135deg, #8448fc, #8b5cf6);
    display: flex; align-items: center; justify-content: center; color: #fff;
  }
  .js-auth-txt { font-size: 12px; color: #94a3b8; margin: 0 0 16px; }
  .js-google-btn {
    display: inline-flex; align-items: center; gap: 8px;
    padding: 10px 20px; background: #fff; border-radius: 8px;
    font-size: 12px; font-weight: 600; color: #333; transition: all 0.3s;
  }
  .js-google-btn.pressed { transform: scale(0.95); box-shadow: 0 1px 4px rgba(0,0,0,0.3); }
  .js-auth-ok {
    display: flex; align-items: center; gap: 5px; justify-content: center;
    margin-top: 14px; color: #34d399; font-size: 12px; font-weight: 600;
    animation: js-fade 0.4s ease;
  }

  /* Verify screen */
  .js-verify-list { width: 100%; display: flex; flex-direction: column; gap: 10px; margin-top: 4px; }
  .js-verify-row {
    display: flex; align-items: center; gap: 10px;
    padding: 8px 12px; border-radius: 8px;
    background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.05);
    color: #94a3b8; font-size: 12px; font-weight: 500; transition: all 0.4s;
  }
  .js-verify-row.done { background: rgba(4, 120, 87,0.08); border-color: rgba(4, 120, 87,0.2); color: #34d399; }
  .js-vr-icon {
    width: 20px; height: 20px; border-radius: 6px; display: flex; align-items: center; justify-content: center;
    background: rgba(255,255,255,0.04); font-size: 10px; font-weight: 700; color: #94a3b8;
  }
  .js-verify-row.done .js-vr-icon { background: rgba(4, 120, 87,0.15); color: #34d399; }
  .js-badge-pill {
    display: flex; align-items: center; gap: 6px; margin-top: 14px;
    padding: 8px 16px; background: rgba(4, 120, 87,0.1); border: 1px solid rgba(4, 120, 87,0.2);
    border-radius: 10px; color: #34d399; font-size: 12px; font-weight: 700;
    animation: js-pop 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  }

  /* Map screen */
  .js-map-box {
    width: 100%; flex: 1; min-height: 0; border-radius: 10px;
    background: #1a2036; position: relative; overflow: hidden;
    border: 1px solid rgba(255,255,255,0.05);
  }
  .js-map-grid-bg {
    position: absolute; inset: 0;
    background-image:
      linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px);
    background-size: 24px 24px;
  }
  .js-map-pin {
    position: absolute; transform: translate(-50%, -100%);
    color: #dc2626; animation: js-drop 0.35s cubic-bezier(0.16, 1, 0.3, 1);
    filter: drop-shadow(0 1px 3px rgba(220, 38, 38,0.4));
  }
  .js-map-me {
    position: absolute; transform: translate(-50%, -50%);
  }
  .js-me-dot {
    width: 10px; height: 10px; border-radius: 50%; background: #8448fc;
    box-shadow: 0 0 0 3px rgba(132, 72, 252,0.25);
    animation: js-pulse-dot 2s ease infinite;
  }

  /* Bid screen */
  .js-bid-card {
    width: 100%; padding: 10px 12px; border-radius: 8px;
    background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.06);
    flex-shrink: 0;
  }
  .js-bid-card strong { display: block; font-size: 12px; color: #e2e8f0; }
  .js-bid-budget { font-size: 10px; color: #94a3b8; }
  .js-bid-amt-area { margin-top: 12px; text-align: center; }
  .js-bid-label { font-size: 9px; color: #94a3b8; font-weight: 600; text-transform: uppercase; letter-spacing: 1px; }
  .js-bid-amt { display: flex; align-items: baseline; justify-content: center; gap: 1px; margin: 4px 0 8px; }
  .js-bid-dollar { font-size: 20px; font-weight: 700; color: #94a3b8; }
  .js-bid-num { font-size: 36px; font-weight: 800; color: #b45309; letter-spacing: -1px; }
  .js-bid-msg {
    padding: 8px 10px; background: rgba(255,255,255,0.03); border-radius: 6px;
    border: 1px solid rgba(255,255,255,0.05); font-size: 10px; color: #94a3b8;
    font-style: italic; line-height: 1.5; animation: js-fade 0.4s ease;
  }
  .js-bid-sent {
    display: flex; align-items: center; gap: 5px; justify-content: center;
    margin-top: 10px; color: #34d399; font-size: 12px; font-weight: 600;
    animation: js-pop 0.3s ease;
  }

  /* Hired screen */
  .js-notif-card {
    display: flex; align-items: center; gap: 10px; width: 100%;
    padding: 12px; border-radius: 10px;
    background: rgba(4, 120, 87,0.08); border: 1px solid rgba(4, 120, 87,0.18);
    animation: js-pop 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  }
  .js-notif-icon {
    width: 32px; height: 32px; border-radius: 10px; flex-shrink: 0;
    background: rgba(4, 120, 87,0.15); color: #34d399;
    display: flex; align-items: center; justify-content: center;
  }
  .js-notif-card strong { display: block; font-size: 12px; color: #34d399; }
  .js-notif-card p { margin: 1px 0 0; font-size: 10px; color: #94a3b8; }
  .js-chat-mini { width: 100%; display: flex; flex-direction: column; gap: 6px; animation: js-fade 0.4s ease; }
  .js-chat-bub {
    max-width: 78%; padding: 8px 10px; border-radius: 10px; font-size: 10px; line-height: 1.4;
  }
  .js-chat-l { background: rgba(255,255,255,0.05); color: #cbd5e1; align-self: flex-start; border-bottom-left-radius: 3px; }
  .js-chat-r { background: rgba(132, 72, 252,0.18); color: #c7d2fe; align-self: flex-end; border-bottom-right-radius: 3px; }
  .js-escrow-pill {
    display: flex; align-items: center; gap: 5px; justify-content: center;
    padding: 6px 12px; border-radius: 7px;
    background: rgba(132, 72, 252,0.08); border: 1px solid rgba(132, 72, 252,0.14);
    color: #818cf8; font-size: 10px; font-weight: 600; animation: js-fade 0.3s ease;
    margin-top: 8px;
  }

  /* Tracking screen */
  .js-track-map {
    width: 100%; height: 150px; border-radius: 10px;
    background: #1a2036; position: relative; overflow: hidden;
    border: 1px solid rgba(255,255,255,0.05); flex-shrink: 0;
  }
  .js-route-svg { position: absolute; inset: 0; width: 100%; height: 100%; }
  .js-trk-start { position: absolute; transform: translate(-50%, -50%); }
  .js-trk-dot-a { width: 8px; height: 8px; border-radius: 50%; background: #94a3b8; border: 2px solid rgba(255,255,255,0.2); }
  .js-trk-hero {
    position: absolute; transform: translate(-50%, -50%);
    width: 18px; height: 18px; border-radius: 50%;
    background: #8448fc; color: #fff; display: flex; align-items: center; justify-content: center;
    box-shadow: 0 0 0 3px rgba(132, 72, 252,0.25), 0 2px 6px rgba(132, 72, 252,0.35);
    transition: left 0.12s linear, top 0.12s linear; z-index: 2;
  }
  .js-trk-dest { position: absolute; transform: translate(-50%, -100%); color: #047857; filter: drop-shadow(0 1px 3px rgba(4, 120, 87,0.4)); }
  .js-status-row {
    display: flex; align-items: center; gap: 7px; margin-top: 10px;
    padding: 8px 12px; border-radius: 8px;
    background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.05);
    font-size: 11px; color: #94a3b8; font-weight: 500;
  }
  .js-status-led { width: 6px; height: 6px; border-radius: 50%; background: #475569; transition: all 0.3s; }
  .js-status-led.on { background: #047857; box-shadow: 0 0 5px rgba(4, 120, 87,0.5); }
  .js-complete-tag {
    display: flex; align-items: center; gap: 6px; justify-content: center;
    margin-top: 10px; color: #34d399; font-size: 12px; font-weight: 700;
    animation: js-pop 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  }

  /* Paid screen */
  .js-earn-card {
    width: 100%; text-align: center; padding: 16px;
    background: rgba(4, 120, 87,0.06); border: 1px solid rgba(4, 120, 87,0.14);
    border-radius: 12px; animation: js-pop 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  }
  .js-earn-lbl { display: block; font-size: 10px; color: #94a3b8; font-weight: 600; text-transform: uppercase; letter-spacing: 0.8px; }
  .js-earn-amt { display: block; font-size: 34px; font-weight: 800; color: #34d399; margin: 4px 0 2px; letter-spacing: -1px; }
  .js-earn-note { font-size: 10px; color: #94a3b8; }
  .js-payout-steps { width: 100%; display: flex; flex-direction: column; gap: 8px; margin-top: 12px; animation: js-fade 0.4s ease; }
  .js-pay-step {
    display: flex; align-items: center; gap: 6px;
    font-size: 11px; color: #475569; font-weight: 500; transition: color 0.3s;
  }
  .js-pay-step.done { color: #34d399; }
  .js-paid-pill {
    display: flex; align-items: center; gap: 6px; justify-content: center;
    margin-top: 12px; padding: 8px 16px;
    background: rgba(4, 120, 87,0.1); border: 1px solid rgba(4, 120, 87,0.2);
    border-radius: 10px; color: #34d399; font-size: 12px; font-weight: 700;
    animation: js-pop 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  }

  /* Post a Job screen */
  .js-form-fields { width: 100%; display: flex; flex-direction: column; gap: 8px; margin-top: 2px; }
  .js-form-row {
    padding: 8px 10px; border-radius: 7px;
    background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.06);
    transition: all 0.4s;
  }
  .js-form-row.filled { border-color: rgba(14,165,233,0.2); background: rgba(14,165,233,0.04); }
  .js-form-label { display: block; font-size: 9px; color: #64748b; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; }
  .js-form-val { display: block; font-size: 12px; color: #e2e8f0; font-weight: 500; margin-top: 1px; animation: js-fade 0.3s ease; }
  .js-form-val-sm { font-size: 10px; color: #94a3b8; }
  .js-form-submit {
    display: flex; align-items: center; gap: 5px; justify-content: center;
    margin-top: 10px; color: #34d399; font-size: 12px; font-weight: 600;
    animation: js-pop 0.3s ease;
  }

  /* Review bids screen */
  .js-bids-list { width: 100%; display: flex; flex-direction: column; gap: 8px; margin-top: 2px; }
  .js-bid-row {
    display: flex; align-items: center; justify-content: space-between;
    padding: 8px 10px; border-radius: 8px;
    background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.06);
    animation: js-slide-in 0.35s ease; transition: all 0.3s;
  }
  .js-bid-row.selected { border-color: rgba(132, 72, 252,0.3); background: rgba(132, 72, 252,0.08); }
  .js-bidder-info { display: flex; align-items: center; gap: 8px; }
  .js-bidder-avatar {
    width: 26px; height: 26px; border-radius: 8px;
    background: linear-gradient(135deg, #8448fc, #8b5cf6);
    display: flex; align-items: center; justify-content: center;
    font-size: 11px; font-weight: 700; color: #fff;
  }
  .js-bidder-info strong { display: block; font-size: 11px; color: #e2e8f0; }
  .js-bidder-rating { display: flex; align-items: center; gap: 2px; font-size: 9px; color: #94a3b8; }
  .js-bidder-price { font-size: 14px; font-weight: 700; color: #b45309; }

  /* Hire screen */
  .js-hire-card {
    text-align: center; padding: 16px; width: 100%;
    background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.08);
    border-radius: 12px; animation: js-pop 0.4s ease;
  }
  .js-hire-avatar {
    width: 40px; height: 40px; border-radius: 12px; margin: 0 auto 8px;
    background: linear-gradient(135deg, #8448fc, #8b5cf6);
    display: flex; align-items: center; justify-content: center;
    font-size: 16px; font-weight: 700; color: #fff;
  }
  .js-hire-card strong { display: block; font-size: 14px; color: #e2e8f0; }
  .js-hire-stars { display: flex; gap: 2px; justify-content: center; margin: 4px 0; }
  .js-hire-price { font-size: 20px; font-weight: 800; color: #b45309; }
  .js-hire-btn {
    display: flex; align-items: center; gap: 6px; justify-content: center;
    padding: 10px 20px; border-radius: 10px; width: 100%;
    background: rgba(132, 72, 252,0.15); border: 1px solid rgba(132, 72, 252,0.25);
    color: #818cf8; font-size: 13px; font-weight: 600;
    transition: all 0.3s; animation: js-fade 0.3s ease;
  }
  .js-hire-btn.confirmed { background: rgba(4, 120, 87,0.12); border-color: rgba(4, 120, 87,0.25); color: #34d399; }

  /* Rate screen */
  .js-rate-block { text-align: center; width: 100%; }
  .js-rate-label { font-size: 13px; color: #94a3b8; margin: 0 0 10px; font-weight: 500; }
  .js-rate-stars { display: flex; gap: 4px; justify-content: center; }
  .js-rate-comment {
    margin-top: 14px; padding: 10px 12px; border-radius: 8px;
    background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.06);
    font-size: 11px; color: #94a3b8; font-style: italic; line-height: 1.5;
    animation: js-fade 0.4s ease; width: 100%;
  }
  .js-rate-done {
    display: flex; align-items: center; gap: 6px; justify-content: center;
    margin-top: 12px; color: #34d399; font-size: 13px; font-weight: 700;
    animation: js-pop 0.4s ease;
  }

  /* Info column (right) */
  .js-info-col { width: 200px; flex-shrink: 0; }
  .js-info-card { animation: js-fade 0.4s ease; }
  .js-info-step-num { font-size: 13px; font-weight: 700; margin-bottom: 8px; }
  .js-info-of { color: #475569; }
  .js-info-title { font-size: 24px; font-weight: 800; color: #f1f5f9; margin: 0 0 6px; letter-spacing: -0.5px; line-height: 1.2; -webkit-text-fill-color: #f1f5f9; background: none; background-clip: unset; -webkit-background-clip: unset; }
  .js-info-sub { font-size: 14px; color: #94a3b8; margin: 0; line-height: 1.5; }

  /* Timeline */
  .js-timeline {
    display: flex; align-items: center; gap: 12px;
    max-width: 700px; margin: 32px auto 0; padding: 0 32px;
  }
  .js-tl-btn {
    width: 34px; height: 34px; border-radius: 50%; flex-shrink: 0;
    background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.08);
    color: #e2e8f0; display: flex; align-items: center; justify-content: center;
    cursor: pointer; transition: all 0.2s;
  }
  .js-tl-btn:hover { background: rgba(255,255,255,0.12); }
  .js-tl-sm { width: 28px; height: 28px; }
  .js-tl-bar { flex: 1; display: flex; gap: 2px; height: 4px; }
  .js-tl-seg { height: 100%; border-radius: 2px; background: rgba(255,255,255,0.05); overflow: hidden; }
  .js-tl-seg-fill { height: 100%; border-radius: 2px; transition: width 0.1s linear; }
  .js-tl-time { font-size: 11px; color: #475569; font-weight: 500; font-variant-numeric: tabular-nums; flex-shrink: 0; min-width: 55px; text-align: right; }

  /* Animations */
  @keyframes js-fade { from { opacity: 0; } to { opacity: 1; } }
  @keyframes js-screen-in { from { opacity: 0; transform: scale(0.94); } to { opacity: 1; transform: scale(1); } }
  @keyframes js-pop {
    0% { opacity: 0; transform: scale(0.8); }
    60% { transform: scale(1.04); }
    100% { opacity: 1; transform: scale(1); }
  }
  @keyframes js-drop { from { opacity: 0; transform: translate(-50%, -200%); } to { opacity: 1; transform: translate(-50%, -100%); } }
  @keyframes js-pulse-dot {
    0%, 100% { box-shadow: 0 0 0 3px rgba(132, 72, 252,0.25); }
    50% { box-shadow: 0 0 0 6px rgba(132, 72, 252,0.08); }
  }
  @keyframes js-slide-in { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }

  /* Responsive */
  @media (max-width: 1024px) {
    .js-info-col { display: none; }
    .js-stage { gap: 32px; }
  }
  @media (max-width: 768px) {
    .js-section { padding: 48px 0 36px; }
    .js-section-title { font-size: 26px; }
    .js-stage { flex-direction: column; gap: 24px; min-height: auto; padding: 0 20px; }
    .js-steps-col { display: none; }
    .js-phone { width: 220px; height: 440px; }
    .js-phone-glow { width: 280px; height: 280px; }
    .js-sw-btn { padding: 9px 18px; font-size: 13px; }
    .js-timeline { padding: 0 20px; }
    .js-phone-col { order: 1; }
  }
  @media (max-width: 480px) {
    .js-phone { width: 200px; height: 400px; }
    .js-sw-btn { padding: 8px 14px; font-size: 12px; gap: 4px; }
  }
`;

export default JourneyShowcase;
